import { Menu, Search, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const navItems = [
  { key: 'library', href: '#library' },
  { key: 'news', href: '#news' },
  { key: 'team', href: '#team' },
  { key: 'about', href: '#about' },
] as const

export type HeaderNavItemKey = (typeof navItems)[number]['key']
export type HeaderActionKey = 'search' | 'signIn'

type HeaderProps = {
  disabledNavItems?: HeaderNavItemKey[]
  disabledActions?: HeaderActionKey[]
}

export function Header({
  disabledNavItems = [],
  disabledActions = [],
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const { t } = useTranslation()
  const disabledNavSet = new Set<HeaderNavItemKey>(disabledNavItems)
  const disabledActionSet = new Set<HeaderActionKey>(disabledActions)

  return (
    <header className="relative z-40 border-b border-[var(--color-outline-variant)]/70 bg-[var(--color-surface-container-lowest)]/95 backdrop-blur-sm">
      <div className="mx-auto w-full max-w-[1280px] px-5 md:px-8">
        <div className="flex items-center justify-between py-4">
          <a
            href="#top"
            className="text-base font-semibold text-[var(--color-on-surface)]"
          >
            {t('layout.header.brand')}
          </a>

          <nav className="hidden items-center gap-8 md:flex">
            {navItems.map((item) => (
              disabledNavSet.has(item.key) ? (
                <span
                  key={item.key}
                  aria-disabled="true"
                  className="cursor-not-allowed text-xs font-medium text-[var(--color-label-disabled)]"
                >
                  {t(`layout.header.${item.key}`)}
                </span>
              ) : (
                <a
                  key={item.key}
                  href={item.href}
                  className="text-xs font-medium text-[var(--color-on-surface-variant)] transition-colors hover:text-[var(--color-on-surface)]"
                >
                  {t(`layout.header.${item.key}`)}
                </a>
              )
            ))}
          </nav>

          <div className="hidden items-center gap-4 md:flex">
            <button
              type="button"
              disabled={disabledActionSet.has('search')}
              aria-label={t('layout.header.searchAriaLabel')}
              className="text-[var(--color-on-surface)] transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:text-[var(--color-label-disabled)] disabled:hover:opacity-100"
            >
              <Search size={16} strokeWidth={2.25} />
            </button>

            <button
              type="button"
              disabled={disabledActionSet.has('signIn')}
              className="text-xs font-medium text-[var(--color-on-surface)] transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:text-[var(--color-label-disabled)] disabled:hover:opacity-100"
            >
              {t('layout.header.signIn')}
            </button>
          </div>

          <button
            type="button"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
            aria-label={
              isMobileMenuOpen
                ? t('layout.header.closeMenuAriaLabel')
                : t('layout.header.openMenuAriaLabel')
            }
            className="rounded-md p-2 text-[var(--color-on-surface)] transition-colors hover:bg-[var(--color-surface-container-high)] md:hidden"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

      </div>

      {isMobileMenuOpen ? (
        <div
          id="mobile-navigation"
          className="absolute inset-x-0 top-full border-b border-[var(--color-outline-variant)]/50 bg-[var(--color-surface-container-lowest)] shadow-lg md:hidden"
        >
          <div className="mx-auto w-full max-w-[1280px] space-y-4 px-5 pb-4 pt-4 md:px-8">
            <nav className="flex flex-col gap-3">
              {navItems.map((item) => (
                disabledNavSet.has(item.key) ? (
                  <span
                    key={item.key}
                    aria-disabled="true"
                    className="cursor-not-allowed text-xs font-medium text-[var(--color-label-disabled)]"
                  >
                    {t(`layout.header.${item.key}`)}
                  </span>
                ) : (
                  <a
                    key={item.key}
                    href={item.href}
                    className="w-fit text-xs font-medium text-[var(--color-on-surface-variant)] transition-colors hover:text-[var(--color-on-surface)]"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t(`layout.header.${item.key}`)}
                  </a>
                )
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <button
                type="button"
                disabled={disabledActionSet.has('search')}
                aria-label={t('layout.header.searchAriaLabel')}
                className="text-[var(--color-on-surface)] transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:text-[var(--color-label-disabled)] disabled:hover:opacity-100"
              >
                <Search size={16} strokeWidth={2.25} />
              </button>

              <button
                type="button"
                disabled={disabledActionSet.has('signIn')}
                className="text-xs font-medium text-[var(--color-on-surface)] transition-opacity hover:opacity-70 disabled:cursor-not-allowed disabled:text-[var(--color-label-disabled)] disabled:hover:opacity-100"
              >
                {t('layout.header.signIn')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  )
}
